import type { Menu, PaginationMeta } from '../types/models.js'
import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody =
    (body as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null)
      ?.error ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

export interface MenuListResponse {
  data: Menu[]
  meta: PaginationMeta
}

export interface CreateMenuData {
  name: string
  slug?: string
  description?: string
}

export interface UpdateMenuData {
  name?: string
  description?: string
}

export type MenuLifecycleAction = 'suspend' | 'activate' | 'archive'

export async function getMenus(organizationId: string): Promise<MenuListResponse> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/menus`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load menus')
  return res.json() as Promise<MenuListResponse>
}

export async function getMenu(organizationId: string, menuId: string): Promise<Menu> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/menus/${menuId}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load menu')
  const b = await res.json()
  return b.data
}

export async function createMenu(organizationId: string, data: CreateMenuData): Promise<Menu> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/menus`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to create menu')
  const b = await res.json()
  return b.data
}

export async function updateMenu(organizationId: string, menuId: string, data: UpdateMenuData): Promise<Menu> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/menus/${menuId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update menu')
  const b = await res.json()
  return b.data
}

async function lifecycleMenu(
  organizationId: string,
  menuId: string,
  action: MenuLifecycleAction,
): Promise<Menu> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/menus/${menuId}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to change menu status')
  const b = await res.json()
  return b.data
}

export function suspendMenu(organizationId: string, menuId: string): Promise<Menu> {
  return lifecycleMenu(organizationId, menuId, 'suspend')
}

export function activateMenu(organizationId: string, menuId: string): Promise<Menu> {
  return lifecycleMenu(organizationId, menuId, 'activate')
}

export function archiveMenu(organizationId: string, menuId: string): Promise<Menu> {
  return lifecycleMenu(organizationId, menuId, 'archive')
}